import React from "react";
import { SymbolAvatar } from "@/components/common";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import { Info } from "lucide-react";
import {
  VybeDealType,
  formatDealType,
  formatDirection,
  dealOpenTimeMs,
  dealTypeBuySellClass,
  type VybeDealInternal,
} from "@/vybe-trader-client/trade-history/types";
import { formatHistoryLocalDateTime } from "@/components/history-view/historyDateTimeFormat";

interface DealItemProps {
  deal: VybeDealInternal;
  onClick?: () => void;
}

export default function DealItem({ deal, onClick }: DealItemProps) {
  // Balance / non-trade deal kinds use VybeDealType on `type`, not placedType
  const isBalanceOperation =
    deal.type === VybeDealType.Balance ||
    deal.type === VybeDealType.Credit ||
    deal.type === VybeDealType.Charge ||
    deal.type === VybeDealType.Correction ||
    deal.type === VybeDealType.Bonus ||
    deal.type === VybeDealType.Commission ||
    deal.type === VybeDealType.DailyCommission ||
    deal.type === VybeDealType.MonthlyCommission ||
    deal.type === VybeDealType.DailyAgentCommission ||
    deal.type === VybeDealType.MonthlyAgentCommission ||
    deal.type === VybeDealType.InterestRate;

  // Use formatDealType for display
  const displayType = formatDealType(deal.type);
  const direction = formatDirection(deal.direction);

  const openedAt = formatHistoryLocalDateTime(dealOpenTimeMs(deal));

  const row = (
    <div
      className="flex items-center gap-3 py-2 border-b border-border cursor-pointer px-1"
      onClick={onClick}
    >
      <SymbolAvatar name={isBalanceOperation ? null : deal.symbol} />
      <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {isBalanceOperation ? (
            <span
              className={`font-medium ${
                (deal.profit || 0) >= 0 ? "text-blue-400" : "text-red-400"
              }`}
            >
              {displayType}
            </span>
          ) : (
            <>
              <span className="text-foreground font-medium">{deal.symbol}</span>
              <span className="text-sm">
                <span className={dealTypeBuySellClass(deal.type)}>
                  {displayType}
                </span>
                <span className="text-muted-foreground">, {direction}</span>
              </span>
            </>
          )}
        </div>
        {deal.profit !== undefined && deal.profit != 0 && (
          <span className={deal.profit >= 0 ? "text-blue-400" : "text-red-400"}>
            {deal.profit > 0 ? "" : ""}
            {deal.profit}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 text-muted-foreground text-xs">
        <div className="flex min-w-0 items-center gap-2 truncate">
          {isBalanceOperation ? (
            <span>{deal.comment || "Balance operation"}</span>
          ) : (
            <span>
              {deal.lots} at {deal.openPrice}
            </span>
          )}
        </div>
<span className="shrink-0 font-mono opacity-70">#{deal.ticketNumber}</span>
                <span className="shrink-0 text-right tabular-nums whitespace-nowrap">
          {openedAt}
        </span>
      </div>
      </div>
    </div>
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={() => onClick?.()}>
          <Info className="mr-2 h-4 w-4" />
          Show details
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
